import { LOAD_EFFECTS } from "../../content/loads.js";
import { formatLoadMessage, OVERDUE_MESSAGE } from "../../domain/resolution/messages.js";
import type { LoadEffect, ResolutionEvent } from "../../domain/types/index.js";
import { generatedHappeningArt, spriteHappeningArt } from "./happeningArtMaps.js";

type StageEvent = Extract<ResolutionEvent, { kind: "load" | "overdue" }>;

export type HappeningArtKind = "sprite" | "generated" | "none";

export type HappeningLabItem = {
  id: string;
  art: HappeningArtKind;
  event: StageEvent;
};

function artKindOf(loadId: string): HappeningArtKind {
  if (spriteHappeningArt(loadId)) return "sprite";
  if (generatedHappeningArt(loadId)) return "generated";
  return "none";
}

function loadEventOf(load: LoadEffect): Extract<StageEvent, { kind: "load" }> {
  const fatigueRaw = load.fatigue ?? 0;
  return {
    kind: "load",
    loadId: load.id,
    loadName: load.name,
    fatigueDealt: fatigueRaw,
    fatigueRaw,
    blockUsed: 0,
    progressDelta: load.progress ?? 0,
    nextEnergyModifier: 0,
    nextHandDiscard: load.nextHandDiscard,
    addTemporaryCards: load.addTemporaryCards,
    nextActionDrawLimit: load.nextActionDrawLimit,
    message: formatLoadMessage(load),
  };
}

const RETAINED_TASKS_EVENT: StageEvent = {
  kind: "load",
  loadId: "retained-tasks",
  loadName: "未処理の対応事項",
  fatigueRaw: 2,
  fatigueDealt: 2,
  blockUsed: 0,
  progressDelta: 0,
  nextEnergyModifier: 0,
  message: "手札に残った対応事項により疲労+2。処理するか、手札から外すまで続く。",
};

export function buildHappeningLabItems(): HappeningLabItem[] {
  const loads: LoadEffect[] = Object.values(LOAD_EFFECTS);
  const items: HappeningLabItem[] = loads.map((load) => ({
    id: load.id,
    art: artKindOf(load.id),
    event: loadEventOf(load),
  }));
  items.push({ id: "retained-tasks", art: artKindOf("retained-tasks"), event: RETAINED_TASKS_EVENT });
  const blocked = loadEventOf(LOAD_EFFECTS["weekend-work"]);
  items.push({
    id: "weekend-work-blocked",
    art: artKindOf("weekend-work"),
    event: { ...blocked, blockUsed: 2, fatigueDealt: Math.max(0, blocked.fatigueRaw - 2) },
  });
  items.push({ id: "overdue", art: "none", event: { kind: "overdue", message: OVERDUE_MESSAGE } });
  return items;
}
